/**
 * Copyright (c) 2026
 *
 * Shared SEO helpers for routes: location phrasing, meta description limits,
 * rich text to plain text, and JSON-LD `sameAs` link normalization.
 */
import { ensureAbsoluteWebUrl } from '@web/src/lib/web-url'

/** Google commonly displays ~155–160 characters; stay within a single SERP line when possible. */
export const SEO_META_DESCRIPTION_MAX_CHARS = 160

const STREET_LEVEL_LEADING_NUMBER_RE = /^\d/
const STREET_LEVEL_PO_BOX_RE = /^P\.?\s*O\.?\s*Box\b/i
const STREET_LEVEL_UNIT_PREFIX_RE = /^(Suite|Unit|Apt|Apartment|#)\s*\d/i

/** Strip markup from rich content for meta/JSON-LD (SSR-safe; no DOM). */
export function plainTextFromRichContent(raw: string | null | undefined): string {
  if (!raw?.trim()) return ''
  let s = raw.trim()
  if (s.startsWith('<')) {
    s = s
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
  }
  return s.replace(/\s+/g, ' ').trim()
}

export function truncateMetaDescription(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text
  const sliceMax = maxChars - 1
  const sliced = text.slice(0, sliceMax)
  const lastSpace = sliced.lastIndexOf(' ')
  if (lastSpace > sliceMax * 0.55) {
    return `${sliced.slice(0, lastSpace).trimEnd()}…`
  }
  return `${sliced.trimEnd()}…`
}

/**
 * Mapbox Search returns comma-separated labels (feature types include `place`, `city`, `address`, `poi`).
 * Meta descriptions should not repeat street-level detail: keep a coarse "city, country" (first and last
 * segments after dropping leading `address`-style parts).
 */
function coarseLocationForMetaDescription(location: string): string {
  const trimmed = location.trim()
  if (!trimmed) return ''

  const parts = trimmed
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
  if (parts.length === 0) return ''

  let start = 0
  while (start < parts.length && looksLikeStreetLevelAddress(parts[start])) {
    start += 1
  }

  const tail = parts.slice(start)
  if (tail.length === 0) {
    const only = parts[0]
    return only && !looksLikeStreetLevelAddress(only) ? only : ''
  }
  if (tail.length === 1) {
    return tail[0]
  }
  return `${tail[0]}, ${tail[tail.length - 1]}`
}

/**
 * Short geography clause for meta / JSON-LD `description` (never includes street address).
 * - Approximate (`locationExact: false`): "near City, Country" (broader area than exact pin).
 * - Exact: "in City, Country".
 */
export function geoPhraseForLocationDescription(location: string, locationExact: boolean): string {
  const coarse = coarseLocationForMetaDescription(location)
  if (!coarse) return ''

  if (!locationExact) {
    return `near ${coarse}`
  }

  return `in ${coarse}`
}

/** Heuristic for Mapbox `address`-style leading segments (comma-separated; see coarseLocationForMetaDescription). */
export function looksLikeStreetLevelAddress(location: string): boolean {
  const s = location.trim()
  // Street number at start (North American and many international formatted addresses)
  if (STREET_LEVEL_LEADING_NUMBER_RE.test(s)) return true
  // PO boxes
  if (STREET_LEVEL_PO_BOX_RE.test(s)) return true
  // Common secondary-unit prefixes before a number
  if (STREET_LEVEL_UNIT_PREFIX_RE.test(s)) return true
  return false
}

/** Normalize external URLs for schema.org `sameAs`. */
export function sameAsFromWebLinks(webLinks: string[] | null | undefined): string[] | undefined {
  if (!webLinks?.length) return undefined
  const urls = webLinks.map((link) => ensureAbsoluteWebUrl(link.trim())).filter((url) => url.length > 0)
  return urls.length > 0 ? urls : undefined
}
